import React, { FunctionComponent, useEffect, useState, useRef, MouseEvent } from 'react';
import { connect } from 'react-redux';
import { concat, equals, map, whereEq } from 'ramda';
import Sidebar from '../../components/SidebarFilter';
import { IAppState } from '../../../../common/models/store/IAppState';
import { IFilter } from '../../../../common/models/filter/IFilter';
import {
  fetchCandidatesRoutine,
  fetchCandidateCountersRoutine,
  updateCandidateFilterRoutine
} from '../../routines';
import { IBindingAction, IBindingCallback1 } from '../../../../common/models/callback';
import { ICandidate } from '../../../../common/models/candidate/ICandidate';
import Table from '../../../../components/Table';
import { getColumns, sidebarItems } from './utils';
import SearchInput from '../../../../components/SearchInput';
import { CandidateCounts } from '../../../../common/models/candidate/CandidateCounts';
import { CandidateFilterType } from '../../../../common/enums/CandidateFilterType';
import AssignmentFilter from '../../../../containers/AssignmentFilter';
import { IAssignmentFilter } from '../../../../common/models/filter/IAssignmentFilter';
import { ISelectedRole } from '../../../../common/models/contact/ISelectedRole';
import { IRowAccessor } from '../../../../common/models/table/IRowAccessor';
import { ContactType } from '../../../../common/enums/ContactType';
import {
  showPositionProfileModalRoutine,
  showContactProfileModalRoutine,
  showRequestBuilderModalRoutine
} from '../../../../routines/history';
import { ICounterFilter } from '../../../../common/models/filter/ICounterFilter';
import { User } from '../../../../common/models/user/User';
import CandidatesBulkActions from '../../../../containers/CandidatesBulkActions';
import { AssignmentFilterType } from '../../../../common/enums/AssignmentFilterType';
import { updateCandidateStatusRoutine } from '../../../../containers/CandidatesBulkActions/routines';
import { ICandidateUpdateStatus } from '../../../../common/models/candidate/ICandidateUpdateStatus';
import { CandidateStatus } from '../../../../common/enums/CandidateStatus';
import ArchiveCandidateModal from '../../../../containers/ArchiveCandidateModal';
import { setReportCandidatesRoutine } from '../../../../containers/ShareReportModal/routines';
import NoDataPlaceholder from '../../components/NoDataPlaceholder';
import { ITableRef } from '../../../../common/models/table/ITableRef';

interface ICandidatesPaneProps {
  counters: CandidateCounts;
  candidates: ICandidate[];
  total: number;
  loading: boolean;
  user: User;
  filter?: IFilter;
  assignmentFilter: AssignmentFilterType;
  isCreatingReport: boolean;
  isSendingReminder: boolean;
  isPositionProfileShown: boolean;
  setFilter: IBindingCallback1<{ filter: Partial<IFilter>, assignmentFilter?: AssignmentFilterType}>;
  fetchCandidates: IBindingCallback1<IFilter>;
  fetchCounters: IBindingCallback1<ICounterFilter>;
  showContactProfileModal: IBindingCallback1<ISelectedRole>;
  showPositionProfileModal: IBindingCallback1<string>;
  updateCandidateStatus: IBindingCallback1<ICandidateUpdateStatus>;
  setReportCandidates: IBindingCallback1<string[]>;
  showRequestBuilderModal: IBindingAction;
}

const CandidatesPane: FunctionComponent<ICandidatesPaneProps> = ({
  user,
  total,
  loading,
  filter,
  counters,
  candidates,
  assignmentFilter,
  isCreatingReport,
  isSendingReminder,
  isPositionProfileShown,
  fetchCounters,
  setFilter,
  fetchCandidates,
  setReportCandidates,
  updateCandidateStatus,
  showRequestBuilderModal,
  showContactProfileModal,
  showPositionProfileModal
}) => {
  const noActiveFilter = {
    input: '',
    statusFilter: [CandidateFilterType.AllActive],
    userIds: [user.id],
    teamIds: undefined
  };
  const [selected, setSelected] = useState<ICandidate[]>([]);
  const [archiveCandidateId, setArchiveCandidateId] = useState<string | undefined>();
  const [totalCount, setTotalCount] = useState(total);
  const ref = useRef<ITableRef>(null);

  const updateFilter = (newFilter: Partial<IFilter>, assigmentType?: AssignmentFilterType) => {
    const updatedFilter = { ...filter, ...newFilter };

    if (!equals(updatedFilter, filter) || (assigmentType && assignmentFilter !== assigmentType)) {
      setFilter({ filter: updatedFilter, assignmentFilter: assigmentType });
    }
  };

  const updateStatusFilter = (statusFilter: string[]) => {
    updateFilter({ ...filter, statusFilter: statusFilter as CandidateFilterType[] });
  };

  const updateAssignmentFilter = ({ userIds, teamIds, assigmentType }: IAssignmentFilter) => {
    updateFilter({ ...filter, userIds, teamIds }, assigmentType);
  };

  const onCellClick = ({ accessor, original }: IRowAccessor<ICandidate>, event: MouseEvent<HTMLElement>) => {
    switch (accessor) {
      case 'name':
        showContactProfileModal({
          contactId: original.contactId,
          candidateId: original.id,
          role: ContactType.Candidate
        });
        break;
      case 'position':
        showPositionProfileModal(original.positionId);
        break;
      case 'actions':
        event.stopPropagation();
        break;
      case 'owner':
        if (original.owner) {
          setFilter({
            filter: {
              ...filter,
              userIds: [original.owner.id],
              teamIds: undefined
            },
            assignmentFilter: original.owner.id === user.id ? AssignmentFilterType.Me : AssignmentFilterType.Custom
          });
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!filter) {
      updateFilter({
        from: 0,
        to: 25,
        input: '',
        sort: [{ id: 'requestDate', desc: true }],
        filter: [],
        statusFilter: [CandidateFilterType.AllActive],
        userIds: [user.id],
        teamIds: undefined
      });
    }
  }, []);

  useEffect(() => setTotalCount(total), [total]);

  useEffect(() => {
    if ((!isCreatingReport || !isSendingReminder) && !isPositionProfileShown) {
      ref.current?.deselectRows();
    }
  }, [isCreatingReport, isSendingReminder]);

  useEffect(() => {
    if (counters && filter) {
      const { filter: f, userIds, teamIds, input } = filter;
      fetchCounters({ filter: f, userIds, teamIds, input });
      fetchCandidates(filter);
    }
  }, [filter]);

  useEffect(() => {
    if (!counters && filter) {
      const { filter: f, userIds, teamIds, input } = filter;
      fetchCounters({ filter: f, userIds, teamIds, input });
      fetchCandidates(filter);
    }
  }, [counters]);

  if (!filter) return <></>;

  const loadData = (from: number, to: number) => {
    fetchCandidates({ ...filter, from, to });
  };

  const sidebarData = map(p => ({
    ...p,
    count: counters ? counters[p.id as CandidateFilterType] : 0,
    children: p.children
      ? map(c => ({
        ...c,
        count: counters ? counters[c?.id as CandidateFilterType] : 0
      }), p.children)
      : null
  }), sidebarItems);

  const unarchiveCandidate = (id: string) => updateCandidateStatus({ ids: [id], status: CandidateStatus.Active });

  const onRunReport = (id: string) => setReportCandidates([id]);

  const noDataPlaceholder = (
    <NoDataPlaceholder
      variant={
        whereEq(noActiveFilter, filter)
          ? 'noActiveCandidates'
          : 'default'
      }
      onButtonClick={showRequestBuilderModal}
    />
  );

  return (
    <>
      <ArchiveCandidateModal
        isShown={Boolean(archiveCandidateId)}
        onHide={() => setArchiveCandidateId(undefined)}
        candidates={archiveCandidateId ? [archiveCandidateId] : []}
      />
      <div className="db-tab-filter">
        <AssignmentFilter
          defaultCheckboxChecked={
            assignmentFilter === AssignmentFilterType.Custom
              ? concat(filter.userIds || [], filter.teamIds || [])
              : undefined
          }
          defaultRadioChecked={assignmentFilter}
          title="filterByAssignedUser"
          defaultTitle="Filter by assigned user"
          onChange={updateAssignmentFilter}
        />
      </div>
      <div className="db-tab-sidebar">
        <Sidebar
          selectFilters={(filter.statusFilter as string[]) || []}
          items={sidebarData}
          onChange={updateStatusFilter}
        />
      </div>
      <div className="db-tab-header d-flex align-items-end">
        <div className="db-tab-subheader d-flex justify-content-between">
          <SearchInput
            onClear={() => updateFilter({ input: '' })}
            value={filter.input}
            onChange={val => updateFilter({ input: val })}
          />
          <div className="d-flex">
            {selected.length > 0 && <CandidatesBulkActions candidates={selected} />}
          </div>
        </div>
      </div>
      <div className="db-tab-table position-relative">
        <Table
          ref={ref}
          config={{
            columns: getColumns(filter.input, setArchiveCandidateId, unarchiveCandidate, onRunReport),
            data: { items: candidates, total: totalCount },
            onLoad: loadData,
            loading,
            onFilterUpdated: updateFilter,
            filteringEnabled: true,
            sortingEnabled: true,
            rowSelection: 'multiple',
            onSelectionChanged: setSelected,
            onCellClicked: onCellClick,
            defaultSorted: filter.sort,
            noDataPlaceholder
          }}
        />
      </div>
    </>
  );
};

const mapStateToProps = ({
  dashboard: {
    candidates: {
      counters,
      candidates,
      loading,
      total,
      assignmentFilter,
      filter
    }
  },
  user: { user },
  report: { candidateIds },
  sendReminder: { isSending: isSendingReminder },
  positionProfile: { positionId }
}: IAppState) => ({
  counters,
  candidates,
  loading,
  total,
  user: user as User,
  filter,
  assignmentFilter,
  isSendingReminder,
  isCreatingReport: Boolean(candidateIds),
  isPositionProfileShown: Boolean(positionId)
});

const mapDispachToProps = {
  setFilter: updateCandidateFilterRoutine,
  fetchCandidates: fetchCandidatesRoutine,
  fetchCounters: fetchCandidateCountersRoutine,
  showContactProfileModal: showContactProfileModalRoutine,
  showPositionProfileModal: showPositionProfileModalRoutine,
  showRequestBuilderModal: showRequestBuilderModalRoutine,
  updateCandidateStatus: updateCandidateStatusRoutine,
  setReportCandidates: setReportCandidatesRoutine
};

export default connect(mapStateToProps, mapDispachToProps)(CandidatesPane);
